import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Logo } from "../../components/site/Seo";
import { toast } from "sonner";
import { Clock, ListChecks, AlertTriangle } from "lucide-react";

export default function TestInstructions() {
  const { testId } = useParams();
  const nav = useNavigate();
  const [test, setTest] = useState(null);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // fetch via admin? use start endpoint lazily; instead fetch minimal via product tests not available. Use start to get test meta on begin.
    api.get(`/tests/${testId}/meta`).then((r) => setTest(r.data)).catch(() => setTest({ name: "Mock Test" }));
  }, [testId]);

  const begin = async () => {
    setBusy(true);
    try {
      await api.post(`/tests/${testId}/start`);
      nav(`/test/${testId}/attempt`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-ink">
      <header className="glass-nav border-b border-line px-4 h-14 flex items-center"><Logo className="h-7" /></header>
      <div className="container-px py-10 max-w-3xl mx-auto">
        <h1 className="font-heading text-2xl font-bold mb-1">{test?.name || "Test Instructions"}</h1>
        <p className="text-sm text-content-secondary mb-8">Please read the instructions carefully before you begin.</p>
        <div className="card-surface p-6 space-y-4">
          <Info icon={Clock} title="Timing">The test is timed. When the timer ends, the test auto-submits with your saved answers.</Info>
          <Info icon={ListChecks} title="Marking">Each question carries positive marks. Wrong answers may attract negative marking as configured.</Info>
          <Info icon={AlertTriangle} title="Navigation">Use the question palette to move between questions. Your answers autosave — you can safely resume if disconnected.</Info>
          <div className="bg-ink rounded-lg p-4 text-sm text-content-secondary">
            <p className="font-medium text-content mb-2">Palette legend</p>
            <div className="grid grid-cols-2 gap-2">
              <Legend color="bg-status-answered" t="Answered" />
              <Legend color="bg-status-notanswered" t="Not Answered" />
              <Legend color="bg-status-review" t="Marked for Review" />
              <Legend color="bg-status-notvisited" t="Not Visited" />
            </div>
          </div>
        </div>
        <label className="flex items-center gap-2 mt-6 text-sm cursor-pointer">
          <Checkbox checked={agree} onCheckedChange={setAgree} data-testid="instructions-agree" />
          I have read and understood the instructions.
        </label>
        <Button disabled={!agree || busy} onClick={begin} className="btn-ember rounded-full mt-6 h-12 px-8" data-testid="instructions-begin">
          {busy ? "Starting…" : "Begin Test"}
        </Button>
      </div>
    </div>
  );
}
const Info = ({ icon: Icon, title, children }) => (
  <div className="flex gap-3"><Icon className="w-5 h-5 text-ember shrink-0 mt-0.5" /><div><div className="text-sm font-medium">{title}</div><div className="text-sm text-content-secondary">{children}</div></div></div>
);
const Legend = ({ color, t }) => <div className="flex items-center gap-2"><span className={`w-4 h-4 rounded ${color}`} /> {t}</div>;
